import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/**
 * Outstanding — a friendly, always-available render of `_master/OUTSTANDING.md`, the working list
 * every Claude session maintains. The dashboard can't read files on Evan's Mac, so sessions mirror
 * the file into `admin_doc_snapshots` via `scripts/sync-outstanding.sh` (de-duped by md5) whenever
 * they update it; every push is kept, so the history rail doubles as a backup of the list over time.
 */

type SnapshotMeta = { id: number; created_at: string; bytes: number; first_line: string };

/**
 * A deliberately small markdown renderer — headings, rules, quotes, bullets, bold/italic/code,
 * links — tuned to how OUTSTANDING.md is actually written. No dependency, no HTML injection:
 * everything renders through React text nodes.
 */
function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // tokenize `code`, **bold**, *italic*, [label](url) — in that precedence
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-${k++}`;
    if (tok.startsWith('`')) {
      out.push(
        <code key={key} className="rounded bg-white/[0.07] px-1 py-0.5 text-[0.92em] text-[#b79cff]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith('**')) {
      out.push(
        <strong key={key} className="font-semibold text-white">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith('[')) {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      out.push(
        mm && /^https?:/.test(mm[2]) ? (
          <a key={key} href={mm[2]} target="_blank" rel="noreferrer" className="text-accent underline">
            {mm[1]}
          </a>
        ) : (
          <span key={key}>{mm ? mm[1] : tok}</span>
        ),
      );
    } else {
      out.push(
        <em key={key} className="italic text-textPrimary">
          {tok.slice(1, -1)}
        </em>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ source }: { source: string }) {
  const blocks = useMemo(() => {
    const lines = source.split('\n');
    const nodes: React.ReactNode[] = [];
    let quote: string[] = [];
    let list: string[] = [];
    const flushQuote = (key: string) => {
      if (quote.length === 0) return;
      const q = quote;
      quote = [];
      nodes.push(
        <blockquote
          key={key}
          className="my-3 border-l-2 border-accent/50 bg-accent/[0.06] py-2 pl-4 pr-3 text-[13px] leading-relaxed text-muted"
        >
          {q.map((l, i) => (
            <div key={i}>{inline(l, `${key}-${i}`)}</div>
          ))}
        </blockquote>,
      );
    };
    const flushList = (key: string) => {
      if (list.length === 0) return;
      const items = list;
      list = [];
      nodes.push(
        <ul key={key} className="my-2 space-y-1 pl-5">
          {items.map((l, i) => (
            <li key={i} className="list-disc text-[13px] leading-relaxed text-muted marker:text-accent">
              {inline(l, `${key}-${i}`)}
            </li>
          ))}
        </ul>,
      );
    };
    lines.forEach((raw, i) => {
      const key = `b${i}`;
      const line = raw.replace(/\s+$/, '');
      const isQuote = /^>\s?/.test(line);
      const isBullet = /^\s*[-*]\s+/.test(line);
      if (!isQuote) flushQuote(`q${i}`);
      if (!isBullet) flushList(`l${i}`);
      if (isQuote) {
        quote.push(line.replace(/^>\s?/, ''));
        return;
      }
      if (isBullet) {
        list.push(line.replace(/^\s*[-*]\s+/, ''));
        return;
      }
      if (/^---+\s*$/.test(line)) {
        nodes.push(<hr key={key} className="my-4 border-white/10" />);
        return;
      }
      const h = /^(#{1,4})\s+(.*)$/.exec(line);
      if (h) {
        const level = h[1].length;
        const cls =
          level === 1
            ? 'mt-6 text-[20px] font-semibold text-white'
            : level === 2
              ? 'mt-6 text-[16px] font-semibold text-white'
              : level === 3
                ? 'mt-5 text-[14px] font-semibold text-[#b79cff]'
                : 'mt-4 text-[13px] font-semibold text-textPrimary';
        nodes.push(
          <div key={key} className={cls}>
            {inline(h[2], key)}
          </div>,
        );
        return;
      }
      if (line.trim() === '') return;
      nodes.push(
        <p key={key} className="my-1.5 text-[13px] leading-relaxed text-muted">
          {inline(line, key)}
        </p>,
      );
    });
    flushQuote('q-end');
    flushList('l-end');
    return nodes;
  }, [source]);

  return <div className="min-w-0">{blocks}</div>;
}

export function OutstandingScreen() {
  const [snaps, setSnaps] = useState<SnapshotMeta[] | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const { data, error: e } = await supabase.rpc('admin_list_doc_snapshots', {
      p_doc: 'outstanding',
      p_limit: 100,
    });
    if (e) {
      setError(e.message);
      return;
    }
    const rows = (Array.isArray(data) ? data : []) as SnapshotMeta[];
    setSnaps(rows);
    if (rows.length > 0) setActiveId((prev) => prev ?? rows[0].id);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (activeId == null) return;
    let alive = true;
    setContent(null);
    void supabase.rpc('admin_get_doc_snapshot', { p_id: activeId }).then(({ data, error: e }) => {
      if (!alive) return;
      if (e) setError(e.message);
      else setContent(typeof data === 'string' ? data : '');
    });
    return () => {
      alive = false;
    };
  }, [activeId]);

  const latestId = snaps?.[0]?.id ?? null;
  const active = snaps?.find((s) => s.id === activeId) ?? null;

  return (
    <div className="admin-page mx-auto max-w-6xl">
      <PageHeader
        title="Outstanding"
        subtitle="The working list every Claude session maintains (_master/OUTSTANDING.md), mirrored here on every update — with the full history as backup."
        actions={
          <button type="button" onClick={() => void load()} className="admin-btn admin-btn-secondary admin-btn-sm">
            Refresh
          </button>
        }
      />
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {snaps === null ? (
        <SkeletonRows rows={8} />
      ) : snaps.length === 0 ? (
        <EmptyState
          title="No snapshots yet"
          hint="A Claude session pushes one with scripts/sync-outstanding.sh after updating _master/OUTSTANDING.md."
        />
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="admin-card min-w-0 flex-1 !p-6">
            {active ? (
              <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-white/10 pb-3 text-[11px] text-mutedDim">
                {activeId === latestId ? (
                  <span className="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-semibold text-accent">
                    Latest
                  </span>
                ) : (
                  <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-semibold text-amber-400">
                    Historical snapshot
                  </span>
                )}
                <span>
                  {new Date(active.created_at).toLocaleString()} · {Math.round(active.bytes / 1024)} KB
                </span>
              </div>
            ) : null}
            {content === null ? <SkeletonRows rows={10} /> : <Markdown source={content} />}
          </div>
          <div className="w-full shrink-0 lg:w-64">
            <div className="admin-eyebrow mb-2">History</div>
            <div className="space-y-1">
              {snaps.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveId(s.id)}
                  data-active={s.id === activeId}
                  className="block w-full rounded-lg border border-transparent px-3 py-2 text-left transition-colors hover:bg-white/[0.04] data-[active=true]:border-accent/40 data-[active=true]:bg-accent/[0.08]"
                >
                  <div className="text-[12px] text-textPrimary">
                    {new Date(s.created_at).toLocaleDateString()}{' '}
                    <span className="text-mutedDim">
                      {new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="text-[10.5px] text-mutedDim">
                    {timeAgo(s.created_at)} · {Math.round(s.bytes / 1024)} KB
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
